import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';
import type {
  CustomerAgingRow,
  KpiSummary,
  TopProductRow,
} from '../data/report';

/**
 * Read-only client for `/api/Report/*` (added 2026-06-29). The
 * controller returns pre-aggregated payloads — the panel reports
 * call these instead of pulling every invoice / item / customer and
 * doing the math in JS.
 *
 * Date inputs accept ISO strings (`YYYY-MM-DD` or full ISO-8601).
 * The backend interprets `from` / `to` / `asOfDate` in tenant local
 * time, same convention as the existing list endpoints.
 *
 * Callers should keep the legacy client-side aggregation as a
 * fallback for older backends that don't expose this controller
 * (catch a 404 and recompute locally).
 */
export interface ReportService {
  /**
   * Headline dashboard tiles for a date range. Revenue / paid /
   * outstanding come from non-cancelled invoices in [from, to];
   * orderCount is non-cancelled sales orders; expenseTotal is
   * expenses recorded in the same window. `netProfit = revenue -
   * expenseTotal`, `avgOrderValue = revenue / orderCount` (0 when
   * orderCount is 0).
   */
  getKpi: (from: string, to: string) => Promise<KpiSummary>;
  /**
   * Top products by revenue within the window. `limit` defaults to
   * 50 server-side; pass a custom value if a report needs a longer
   * tail. Items are pre-sorted descending by revenue.
   */
  getTopProducts: (from: string, to: string, limit?: number) => Promise<TopProductRow[]>;
  /**
   * Outstanding balance buckets per customer, computed as of the
   * given date. Only customers with `total > 0` are returned.
   */
  getCustomerAging: (asOfDate: string) => Promise<CustomerAgingRow[]>;
}

export function createReportService(client: AxiosInstance): ReportService {
  return {
    async getKpi(from, to) {
      const res = await client.get<ObjectResponse<KpiSummary>>('/Report/Dashboard/KPI', {
        params: { from, to },
      });
      return unwrap(res.data);
    },
    async getTopProducts(from, to, limit = 50) {
      const res = await client.get<ObjectResponse<TopProductRow[]>>('/Report/TopProducts', {
        params: { from, to, limit },
      });
      return unwrapItems(res.data);
    },
    async getCustomerAging(asOfDate) {
      const res = await client.get<ObjectResponse<CustomerAgingRow[]>>('/Report/CustomerAging', {
        params: { asOfDate },
      });
      return unwrapItems(res.data);
    },
  };
}
